/**
 * `<UserPicker/>` — dev-mode landing page.
 *
 * Renders when there's no dev account id in localStorage. Hits the
 * unauthenticated `/api/dev/accounts` endpoint and lets the operator
 * click to "sign in" as that account. This is the first thing a fresh
 * visitor sees in dev mode (Stream A acceptance criterion).
 *
 * In Stream C this page is replaced by Clerk's hosted sign-in surface;
 * the route stays mounted under `/dev/sign-in` for fall-back testing.
 *
 * #3005 — `mode="add"` (`/sign-in/add` in the dev shim): **Add another
 * account**. A pick records the account in the dev list and runs the
 * account switch (clear the caches, the new dev id, a full load of `/`).
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ShieldCheck, Sparkles, UserCircle2, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { apiFetch } from "../api/client";
import type { paths } from "../api/schema.gen";
import { Logo } from "../components/Logo";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Skeleton,
} from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { noteDevRecentAccount } from "../lib/accountSwitch";

type DevAccount =
  paths["/api/dev/accounts"]["get"]["responses"]["200"]["content"]["application/json"][number];

export function UserPicker({ mode = "sign-in" }: { mode?: "sign-in" | "add" } = {}) {
  const navigate = useNavigate();
  const { signIn, switchAccount } = useAuth();
  const adding = mode === "add";
  const accountsQuery = useQuery({
    queryKey: ["dev", "accounts"],
    queryFn: () => apiFetch<DevAccount[]>("/api/dev/accounts"),
  });

  function pick(acc: DevAccount) {
    if (adding && switchAccount) {
      noteDevRecentAccount({
        id: acc.id,
        name: acc.display_name || acc.email || acc.id,
        email: acc.email ?? null,
      });
      void switchAccount(acc.id);
      return;
    }
    signIn(acc.id);
    navigate("/", { replace: true });
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col gap-10 px-4 py-12 sm:px-6">
      <header className="flex flex-col gap-3">
        <Logo />
        <h1 className="text-3xl font-semibold tracking-tight text-fg sm:text-4xl">
          {adding ? "Add another account" : "Pick a dev account to continue"}
        </h1>
        <p className="max-w-xl text-fg-muted">
          Dev mode — pick a seeded account to continue. Real Google sign-in is wired and switches on
          once we set the Clerk keys.
        </p>
        <div className="flex flex-wrap gap-2 text-xs text-fg-subtle">
          <Badge tone="accent">
            <ShieldCheck className="h-3 w-3" aria-hidden="true" />
            dev shim only
          </Badge>
          <Badge>{import.meta.env.MODE}</Badge>
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <Button asChild size="sm">
            <Link to="/demo">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Try the demo
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </header>

      <section>
        {accountsQuery.isLoading ? (
          <div className="grid gap-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        ) : accountsQuery.isError ? (
          // #1740 — degrade gracefully instead of surfacing a raw HTTP
          // error. This state is normal on a static preview build with
          // no backend (no Clerk key baked in AND no dev API), which is
          // exactly what a founder-preview link looks like; the raw
          // "error 404" read as breakage.
          <ErrorState
            title="Sign-in isn't available on this preview"
            message="This build runs without a sign-in backend — the dev-accounts API isn't reachable and no Clerk key is configured. Browse the demo instead, or retry if the dev API is still starting."
            onRetry={() => accountsQuery.refetch()}
          />
        ) : accountsQuery.data && accountsQuery.data.length > 0 ? (
          <ul className="grid gap-3">
            {accountsQuery.data.map((acc) => (
              <li key={acc.id}>
                <Card className="transition-colors hover:border-accent/40 hover:bg-bg-elevated">
                  <CardHeader className="flex-row items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
                      <UserCircle2 className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-base">
                        {acc.display_name || acc.email || "(no name)"}
                      </CardTitle>
                      <p className="text-xs text-fg-subtle">
                        {acc.email || "no email"} · {acc.plan} plan
                      </p>
                    </div>
                    <Button size="sm" onClick={() => pick(acc)}>
                      Continue
                    </Button>
                  </CardHeader>
                  <CardContent className="pb-4">
                    {acc.memberships.length === 0 ? (
                      <p className="text-xs text-fg-faint">
                        No team memberships — this account will land on an empty home page.
                      </p>
                    ) : (
                      <ul className="flex flex-wrap gap-2">
                        {acc.memberships.map((m) => (
                          <li key={`${acc.id}-${m.team_id}`}>
                            <Badge tone="accent">
                              <Users className="h-3 w-3" aria-hidden="true" />
                              {m.team_name} · {m.role}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={Users}
            title="No dev accounts seeded"
            description="Run the seed script: `python scripts/seed.py --with-fake-accounts --with-paid-account`."
          />
        )}
      </section>
    </main>
  );
}
